import { Children, isValidElement, type ComponentType, type ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';

/**
 * Replacement for React.lazy for routed pages.
 * - Renders synchronously once its module has been loaded (React.lazy suspends again for a tick even then).
 * - Exposes `preload()`, used by `withRoutePreload` so the router loads the page module *before* it commits a
 *   navigation (no blank main with a Suspense fallback) and before hydration/server render.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- page components have heterogeneous props
type AnyProps = any;
export type LazyPage = ((props: AnyProps) => ReactNode) & { preload: () => Promise<void> };

export function lazyPage(load: () => Promise<{ default: ComponentType<AnyProps> }>): LazyPage {
  let Loaded: ComponentType<AnyProps> | null = null;
  let failure: unknown = null;
  let pending: Promise<void> | null = null;

  const preload = (): Promise<void> => {
    pending ??= load().then(
      (mod) => { Loaded = mod.default; },
      (error: unknown) => { failure = error; pending = null; },
    );
    return pending;
  };

  const Page = ((props: AnyProps) => {
    if (failure) {
      const error = failure;
      failure = null; // a retry (next render) may load again
      throw error;
    }
    if (!Loaded) throw preload();
    const Component = Loaded;
    return <Component {...props} />;
  }) as LazyPage;
  Page.preload = preload;
  return Page;
}

const collectPreloads = (node: ReactNode, out: Array<() => Promise<void>>): void => {
  if (!isValidElement(node)) return;
  const type: unknown = node.type;
  const preload = typeof type === 'function' ? (type as { preload?: () => Promise<void> }).preload : undefined;
  if (typeof preload === 'function') out.push(preload);
  const children = (node.props as { children?: ReactNode }).children;
  Children.toArray(children).forEach((child) => collectPreloads(child, out));
};

/**
 * Gives every route whose element contains a lazyPage a `lazy` hook that loads those modules. React Router
 * waits for it before committing a navigation; the static handler (build) and `preResolveLazyRoutes`
 * (hydration) resolve it before rendering.
 */
export const withRoutePreload = (routes: RouteObject[]): RouteObject[] =>
  routes.map((route) => {
    const next: RouteObject = { ...route };
    if (route.children) next.children = withRoutePreload(route.children);
    const preloads: Array<() => Promise<void>> = [];
    if (route.element) collectPreloads(route.element, preloads);
    if (preloads.length > 0 && !route.lazy) {
      (next as { lazy?: () => Promise<Record<string, never>> }).lazy = async () => {
        await Promise.all(preloads.map((preload) => preload()));
        return {};
      };
    }
    return next;
  });
